import Modal from "../../components/modal/Modal";
import { useTranslation } from "@me8eon/translation";
import TileComponent from "../../components/tile-component";
import TabComponent from "../../components/tab-component";
import NewTabComponent from "../../components/tab-component/NewTabComponent";
import "./TilePage.scss";
import Organize from "../../components/organize-tile-page/Organize";
import PersonalizeDashboard from "../../components/organize-tile-page/PersonalizeDashboard";
import React, { useEffect, useMemo, useState } from "react";
import LoaderComponent from "../../components/loader-component/LoaderComponent";
import useSearchStore from "../../store/searchStore";
import { useFirstName } from "../../components/ai-assist-refactored/hooks/useFirstName";
import { useNavigate } from "react-router-dom";
import { AiAssistantPromptArea } from "../../modules/AiAssist/organisms/AiAssistantPromptArea/AiAssistantPromptArea";
import { useQuestionSuggestions, isDataAs } from "@me8eon/async";
import { AgentSelectorTextAreaPlugin } from "../../features/AiPrompt/plugins/AgentSelectorTextAreaPlugin/AgentSelectorTextAreaPlugin";
import { useAgentSelector, useDoAi } from "@me8eon/react_intent";
import { LlmModelSelectorPlugin } from "../../features/AiPrompt/plugins/LlmModelSelectorPlugin/LlmModelSelectorPlugin";
import { llmModelSelectorFFName } from "../../hooks/useLlmModelSelectorFlag";
import { useAsyncSessionAndMessages, useMakeNewSession } from "@me8eon/sessions_messages";
import { useFeedback } from "@me8eon/feedback";
import { useJwtToken, useUserData } from "@me8eon/react_authentication";
import { useOnceWhen } from "@me8eon/debounce";
import { createOpenAiClient } from "@me8eon/openai/src/openai.client";
import axios from "axios";
import { EnvironmentConfig } from "@me8eon/config";
import {
    ImageUploaderTextAreaPlugin
} from "../../features/AiPrompt/plugins/ImageUploaderTextAreaPlugin/ImageUploaderTextAreaPlugin";
import {
    CompassSourceSelectorTextAreaPlugin
} from "../../features/AiPrompt/plugins/CompassSourceSelectorTextAreaPlugin/CompassSourceSelectorTextAreaPlugin";
import { useFeatureFlag } from "@me8eon/featureflags";
import { compassPersonalizationFFName } from "../../features/AiPrompt/plugins/CompassSourceSelectorTextAreaPlugin/compassPersonalization.feature.flag";
import { useSearchTextFilter } from "@me8eon/searchtext_filters";
import { isHigherEnv } from "../../util/common-util";

const TilePage = () => {
    const firstName = useFirstName()
    const t = useTranslation();
    const [value, setValue] = React.useState("");
    const navigate = useNavigate();
    const [, setQuery] = useSearchTextFilter();
    const { currentRole, featureFlagEnabled } = useUserData();

    const {
        isPageLoading,
        displayedModal,
        isEditMode,
        showModal,
        favData,
        clearQuestionData
    } = useSearchStore();
    const handleAiQuery = useDoAi();
    const [state] = useAsyncSessionAndMessages();
    const makeNewSession = useMakeNewSession();
    const feedbackFn = useFeedback();
    const jwt = useJwtToken()

    const { favTabs, favApps } = favData;
    const showTabComponent = favApps.length > 0;
    const activeTabObj = favTabs.filter((tab) => tab.active);
    const activeTab = activeTabObj.length > 0 ? activeTabObj[0].name : "";

    const setNewQueryInUrl = (newQuery: string) => {
        const searchParams = new URLSearchParams(location.search);
        if (newQuery) {
            searchParams.set("q", newQuery);
        }
        const queryString = searchParams.toString();
        return queryString ? queryString : null;
    };

    useEffect(() => {
        setQuery("");
    }, []);

    useOnceWhen(isDataAs(state), () => {
        makeNewSession("");
        setQs([]);
    });

    const submitTextArea = (val: string) => {
        if (!val) return;
        handleAiQuery(val);
        clearQuestionData();
        feedbackFn({ source: "query", page: "simple", query: val, role: currentRole || "unknown" });
        setQuery("");
        navigate(`/ai?${setNewQueryInUrl(val)}`);
    };

    const [qs, setQs] = useQuestionSuggestions();
    const [imageSuggestions, setImageSuggestions] = useState<string[]>([]);

    const aiClient = createOpenAiClient({
        axios,
        baseURL: EnvironmentConfig.nodeService,
        apiKey: jwt
    });

    const { selectedAgent } = useAgentSelector();
    const showImage = isHigherEnv() ? featureFlagEnabled : true;
    const compassPersonalizationFF = useFeatureFlag(compassPersonalizationFFName);
    const llmSelectorEnabled = useFeatureFlag(llmModelSelectorFFName);

    const promptArea = useMemo(() => <AiAssistantPromptArea
        value={value}
        onChange={setValue}
        onSubmit={submitTextArea}
        isTilePage
        imageSuggestions={imageSuggestions}
        plugins={[
            <ImageUploaderTextAreaPlugin
                ai={aiClient}
                setSuggestions={setImageSuggestions}
            />,
            ...(showImage ? [
                <AgentSelectorTextAreaPlugin isBottom />,
                ...(llmSelectorEnabled ? [<LlmModelSelectorPlugin isBottom />] : [])
            ] : []),
            ...(compassPersonalizationFF && selectedAgent?.name === "compass" ? [
                <CompassSourceSelectorTextAreaPlugin isBottom />
            ] : [])
        ]}        
    />, [value, setValue, submitTextArea, aiClient, setQs]);

    return (
        <div className="tilePage">
            <LoaderComponent isLoading={isPageLoading} />
            <Modal
                toggleModal={displayedModal === "addNewTab" || displayedModal === "personalizeDashboard"}
                closeModal={() => showModal("")}
                isCloseIcon
                closeOnBackdrop={false}
            >
                {displayedModal === "addNewTab" && (
                    <NewTabComponent favTabs={favTabs} />
                )}
                {displayedModal === "personalizeDashboard" && (
                    <PersonalizeDashboard favTabs={favTabs} favApps={favApps} />
                )}
            </Modal>
            <div id="tileTour6">
                <div className={`${isEditMode ? "edit-mode--opaque" : ""}`}>
                    {showTabComponent && (
                        <TabComponent favTabs={favTabs} isEditMode={isEditMode} />
                    )}
                </div>
                <div className="tile-background" style={{ backgroundColor: favApps.length > 0 ? 'var(--super-light-bg)' : 'transparent' }}>
                    {favApps.length > 0 && <Organize favTabs={favTabs} isEditMode={isEditMode} />}
                </div>
            </div>
            <div className="tile-background" style={{ backgroundColor: favApps.length > 0 ? 'var(--super-light-bg)' : 'var(--super-light-bg)' }}>
                <TileComponent
                    favApps={favApps}
                    activeTab={activeTab}
                    isEditMode={isEditMode}
                    favTabs={favTabs}
                />
            </div>
        </div>
    );
};
export default TilePage;
